import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import client from '../api/client'
import { useAuth } from '../hooks/useAuth'
import { useSubscription } from '../contexts/SubscriptionContext'
import type { Plan } from '../types'
import BillingToggle, { type BillingCycle as Cycle } from './pricing/BillingToggle'

const PURPLE = '#7C3AED'
const PURPLE_LIGHT = '#A78BFA'
const GOLD = '#FBBF24'
const SURFACE = '#111118'
const BORDER = 'rgba(255,255,255,0.10)'
const TEXT_MUTED = '#9CA3AF'

export default function PricingSection() {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const { summary } = useSubscription()
  const currentPlanSlug =
    summary && summary.has_subscription && summary.status === 'active'
      ? summary.plan_slug
      : null
  const [plans, setPlans] = useState<Plan[]>([])
  const [error, setError] = useState<string | null>(null)
  const [cycle, setCycle] = useState<Cycle>('monthly')

  useEffect(() => {
    let cancelled = false
    client.get<Plan[]>('/api/plans/')
      .then((res) => { if (!cancelled) setPlans(res.data) })
      .catch(() => { if (!cancelled) setError('Could not load plans.') })
    return () => { cancelled = true }
  }, [])

  const getPlan = (slug: string) => plans.find((p) => p.slug === slug)

  const allAccess = getPlan('all_access')
  const agency = getPlan('agency')
  const individualCore = getPlan('individual_core')
  const industryPack = getPlan('industry_pack')

  const showAllAccessFounder = (allAccess?.founder_spots_remaining ?? 0) > 0
  const showAgencyFounder = (agency?.founder_spots_remaining ?? 0) > 0
  const showFounderSection = showAllAccessFounder || showAgencyFounder

  const handleSelectPlan = (slug: string, planCycle: 'monthly' | 'annual' | 'founder') => {
    if (!isAuthenticated) {
      sessionStorage.setItem('pendingCheckout', JSON.stringify({
        plan_slug: slug,
        cycle: planCycle,
      }))
      navigate('/register?next=checkout')
      return
    }
    navigate(`/checkout/v2?plan=${slug}&cycle=${planCycle}`)
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 20px' }}>
        <p style={{ color: '#EF4444' }}>{error}</p>
        <button onClick={() => location.reload()} style={btnPrimary}>Retry</button>
      </div>
    )
  }

  if (plans.length === 0) {
    return <PricingSkeleton />
  }

  return (
    <div style={{ fontFamily: 'Inter, sans-serif', color: '#fff' }}>
      {/* Hero */}
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <p style={{ fontFamily: 'Syne, sans-serif', fontSize: 20, fontWeight: 700, margin: '0 0 6px' }}>
          Pay monthly. Cancel anytime.
        </p>
        <p style={{ color: TEXT_MUTED, fontSize: 15, margin: 0 }}>
          Save 40% going annual — billed monthly, no big upfront payment.
        </p>
      </div>

      {/* Toggle */}
      <div style={{ marginBottom: 40 }}>
        <BillingToggle cycle={cycle} onChange={setCycle} />
      </div>

      {/* Row 1 — Core plans (H1/H2) */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 20,
        marginBottom: 20,
      }}>
        <PrimaryCard
          plan={individualCore}
          cycle={cycle}
          isCurrent={currentPlanSlug === 'individual_core'}
          features={[
            'Pick any 1 core agent',
            'Full access to that agent',
            'Add industry specialists later',
            'Cancel anytime',
          ]}
          showAddonHint
          onSelect={() => handleSelectPlan('individual_core', cycle)}
        />
        <PrimaryCard
          plan={getPlan('bundle_3')}
          cycle={cycle}
          isCurrent={currentPlanSlug === 'bundle_3'}
          features={[
            'Any 3 core agents (mix and match)',
            'Flat price regardless of tier mix',
            'Save vs individual pricing',
            'Cancel anytime',
          ]}
          showAddonHint
          onSelect={() => handleSelectPlan('bundle_3', cycle)}
        />
        <PrimaryCard
          plan={getPlan('bundle_6')}
          cycle={cycle}
          isCurrent={currentPlanSlug === 'bundle_6'}
          features={[
            'Any 6 core agents (mix and match)',
            'Switch between any of 10 cores',
            'Save vs individual pricing',
            'Cancel anytime',
          ]}
          showAddonHint
          onSelect={() => handleSelectPlan('bundle_6', cycle)}
        />
        <PrimaryCard
          plan={allAccess}
          cycle={cycle}
          highlighted
          badge="★ Most Popular"
          isCurrent={currentPlanSlug === 'all_access'}
          features={[
            'All 10 core agents',
            'All 4 agents in your industry',
            'Pick your industry at signup',
            'Cancel anytime',
            ...(showAllAccessFounder ? ['Founder pricing available below'] : []),
          ]}
          onSelect={() => handleSelectPlan('all_access', cycle)}
        />
      </div>

      {/* Row 2 — Industry & Agency plans (H1) */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: 20,
        marginBottom: 56,
      }}>
        <PrimaryCard
          plan={industryPack}
          cycle={cycle}
          isCurrent={currentPlanSlug === 'industry_pack'}
          features={[
            'All 4 agents in your industry',
            'Buy 3, get 1 free',
            'Pick your industry at signup',
            'Cancel anytime',
          ]}
          onSelect={() => handleSelectPlan('industry_pack', cycle)}
        />
        <PrimaryCard
          plan={agency}
          cycle={cycle}
          isCurrent={currentPlanSlug === 'agency'}
          features={[
            'Everything in All-Access',
            '3 team seats included',
            'Manage clients with your team',
            'Add seats as you grow ($39/seat)',
            'Cancel anytime',
          ]}
          onSelect={() => handleSelectPlan('agency', cycle)}
        />
      </div>

      {/* C3 — individual_industry is purchasable via agent/industry detail pages */}
      <p style={{ textAlign: 'center', color: TEXT_MUTED, fontSize: 13, marginBottom: 40 }}>
        Need just one specific agent?{' '}
        <button onClick={() => navigate('/agents')} style={{ background: 'none', border: 'none', color: PURPLE_LIGHT, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, padding: 0, textDecoration: 'underline' }}>
          Browse agents
        </button>
        {' '}or{' '}
        <button onClick={() => navigate('/industries')} style={{ background: 'none', border: 'none', color: PURPLE_LIGHT, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, padding: 0, textDecoration: 'underline' }}>
          browse industries
        </button>
        {' '}to pick a single specialist ($19/mo).
      </p>

      {/* Founder hero */}
      {showFounderSection && (
        <FounderHero
          showAllAccess={showAllAccessFounder}
          showAgency={showAgencyFounder}
          allAccess={allAccess}
          agency={agency}
          currentPlanSlug={currentPlanSlug}
          onSelect={handleSelectPlan}
        />
      )}

      {/* FAQ */}
      <PricingFAQ />
    </div>
  )
}


// ============================================
// Subcomponents
// ============================================

interface PrimaryCardProps {
  plan: Plan | undefined
  cycle: Cycle
  highlighted?: boolean
  badge?: string
  isCurrent?: boolean
  features: string[]
  /** Phase 5e — when true, render an "industry specialists from $19/mo"
   *  footer line. Use for addon-eligible plans (bundle_3, bundle_6,
   *  individual_core) so the user knows they can layer on industry agents
   *  after subscribing. */
  showAddonHint?: boolean
  onSelect: () => void
}

function PrimaryCard({ plan, cycle, highlighted, badge, isCurrent, features, showAddonHint, onSelect }: PrimaryCardProps) {
  if (!plan) return null
  const monthly = plan.monthly_price
  const annual = plan.annual_price
  const displayPrice = cycle === 'annual' ? annual : monthly
  const annualSavings = (monthly - annual) * 12

  return (
    <div style={{
      background: SURFACE,
      border: `${highlighted ? '2px' : '1px'} solid ${highlighted ? PURPLE : BORDER}`,
      borderRadius: 14,
      padding: 28,
      position: 'relative',
      transition: 'transform 0.2s, box-shadow 0.2s',
      boxShadow: highlighted ? `0 0 30px ${PURPLE}33` : 'none',
    }}>
      {badge && (
        <div style={{
          position: 'absolute',
          top: -12,
          left: '50%',
          transform: 'translateX(-50%)',
          background: PURPLE,
          color: '#fff',
          padding: '4px 14px',
          borderRadius: 99,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 0.5,
          textTransform: 'uppercase',
        }}>
          {badge}
        </div>
      )}

      <h3 style={{ fontFamily: 'Syne, sans-serif', fontSize: 22, fontWeight: 700, margin: '0 0 4px' }}>
        {plan.display_name}
      </h3>
      <p style={{ color: TEXT_MUTED, fontSize: 13, minHeight: 32, margin: '0 0 20px' }}>
        {plan.description}
      </p>

      <div style={{ marginBottom: 20 }}>
        {cycle === 'annual' && (
          <div style={{ color: TEXT_MUTED, fontSize: 13, textDecoration: 'line-through', marginBottom: 2 }}>
            ${monthly.toFixed(0)}/mo
          </div>
        )}
        <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800 }}>
          <span style={{ fontSize: 42 }}>${displayPrice.toFixed(0)}</span>
          <span style={{ fontSize: 16, color: TEXT_MUTED, marginLeft: 4 }}>/mo</span>
        </div>
        <div style={{ color: TEXT_MUTED, fontSize: 12, marginTop: 4 }}>
          {cycle === 'annual'
            ? `billed monthly · save $${annualSavings.toFixed(0)}/year`
            : 'billed monthly · cancel anytime'}
        </div>
      </div>

      <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 14px' }}>
        {features.map((f) => (
          <li key={f} style={{ fontSize: 14, padding: '6px 0', color: '#E5E7EB' }}>
            <span style={{ color: PURPLE_LIGHT, marginRight: 8 }}>✓</span>{f}
          </li>
        ))}
      </ul>

      {showAddonHint && (
        <div style={{
          fontSize: 12,
          color: TEXT_MUTED,
          fontStyle: 'italic',
          marginBottom: 16,
          paddingTop: 10,
          borderTop: `1px dashed ${BORDER}`,
        }}>
          + Industry specialists from $19/mo (add after subscribing)
        </div>
      )}

      {isCurrent ? (
        <CurrentPlanBadge />
      ) : (
        <button onClick={onSelect} style={{ ...btnPrimary, width: '100%' }}>
          Get Started
        </button>
      )}
    </div>
  )
}

interface SecondaryCardProps {
  title: string
  price: string
  subtitle: string
  ctaLabel: string
  isCurrent?: boolean
  onClick: () => void
}

function SecondaryCard({ title, price, subtitle, ctaLabel, isCurrent, onClick }: SecondaryCardProps) {
  return (
    <div style={{
      background: SURFACE,
      border: `1px solid ${BORDER}`,
      borderRadius: 12,
      padding: 20,
    }}>
      <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 18, fontWeight: 700, marginBottom: 4 }}>
        {title}
      </div>
      <div style={{ color: PURPLE_LIGHT, fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
        {price}
      </div>
      <p style={{ color: TEXT_MUTED, fontSize: 13, lineHeight: 1.5, margin: '0 0 16px' }}>
        {subtitle}
      </p>
      {isCurrent ? (
        <CurrentPlanBadge />
      ) : (
        <button onClick={onClick} style={{ ...btnSecondary, width: '100%' }}>
          {ctaLabel}
        </button>
      )}
    </div>
  )
}

interface FounderHeroProps {
  showAllAccess: boolean
  showAgency: boolean
  allAccess: Plan | undefined
  agency: Plan | undefined
  currentPlanSlug: string | null
  onSelect: (slug: string, cycle: 'founder') => void
}

function FounderHero({ showAllAccess, showAgency, allAccess, agency, currentPlanSlug, onSelect }: FounderHeroProps) {
  return (
    <div style={{
      background: `linear-gradient(135deg, ${SURFACE}, rgba(124,58,237,0.10))`,
      border: `1px solid ${PURPLE}`,
      borderRadius: 16,
      padding: '40px 28px',
      marginBottom: 56,
    }}>
      <div style={{ textAlign: 'center', marginBottom: 28 }}>
        <h2 style={{
          fontFamily: 'Syne, sans-serif',
          fontSize: 28,
          fontWeight: 800,
          margin: '0 0 8px',
        }}>
          🚀 Founding Member Pricing
        </h2>
        <p style={{ color: TEXT_MUTED, fontSize: 15, margin: 0 }}>
          Lock in 50% off forever. Only available until spots fill.
        </p>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
        gap: 20,
      }}>
        {showAllAccess && allAccess && (
          <FounderCard
            title="All-Access Founder"
            comparePrice={allAccess.monthly_price}
            founderPrice={allAccess.founder_monthly_price ?? 0}
            cap={allAccess.founder_cap ?? 0}
            remaining={allAccess.founder_spots_remaining ?? 0}
            isCurrent={currentPlanSlug === 'all_access'}
            features={[
              'All 10 core agents + your industry’s 4',
              '50% off forever — locked rate',
              'Industry locked at signup',
              'Permanent founding member status',
            ]}
            onSelect={() => onSelect('all_access', 'founder')}
          />
        )}
        {showAgency && agency && (
          <FounderCard
            title="Agency Founder"
            comparePrice={agency.monthly_price}
            founderPrice={agency.founder_monthly_price ?? 0}
            cap={agency.founder_cap ?? 0}
            remaining={agency.founder_spots_remaining ?? 0}
            isCurrent={currentPlanSlug === 'agency'}
            features={[
              'Everything in All-Access',
              '3 team seats included',
              '50% off forever — locked rate',
              'Permanent founding member status',
            ]}
            onSelect={() => onSelect('agency', 'founder')}
          />
        )}
      </div>

      <p style={{ color: TEXT_MUTED, fontSize: 12, textAlign: 'center', marginTop: 24, marginBottom: 0 }}>
        Founder spots are permanently consumed once claimed — cancellation forfeits the rate.
      </p>
    </div>
  )
}

interface FounderCardProps {
  title: string
  comparePrice: number
  founderPrice: number
  cap: number
  remaining: number
  isCurrent?: boolean
  features: string[]
  onSelect: () => void
}

function FounderCard({ title, comparePrice, founderPrice, cap, remaining, isCurrent, features, onSelect }: FounderCardProps) {
  const claimed = cap - remaining
  const fillPct = cap > 0 ? (claimed / cap) * 100 : 0

  return (
    <div style={{
      background: SURFACE,
      border: `1px solid ${PURPLE}`,
      borderRadius: 14,
      padding: 24,
    }}>
      <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 20, fontWeight: 700, marginBottom: 12 }}>
        {title}
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
        <span style={{ fontFamily: 'Syne, sans-serif', fontSize: 38, fontWeight: 800 }}>
          ${founderPrice.toFixed(0)}
        </span>
        <span style={{ fontSize: 14, color: TEXT_MUTED }}>/mo</span>
        <span style={{ fontSize: 13, color: TEXT_MUTED, textDecoration: 'line-through', marginLeft: 8 }}>
          ${comparePrice.toFixed(0)}
        </span>
      </div>
      <div style={{ color: GOLD, fontSize: 13, fontWeight: 600, marginBottom: 16 }}>
        🔥 {remaining} of {cap} spots remaining
      </div>
      <div style={{
        height: 6,
        background: 'rgba(255,255,255,0.08)',
        borderRadius: 3,
        marginBottom: 20,
        overflow: 'hidden',
      }}>
        <div style={{
          height: '100%',
          width: `${fillPct}%`,
          background: PURPLE,
          transition: 'width 0.3s',
        }} />
      </div>
      <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px' }}>
        {features.map((f) => (
          <li key={f} style={{ fontSize: 13, padding: '4px 0', color: '#E5E7EB' }}>
            <span style={{ color: PURPLE_LIGHT, marginRight: 8 }}>✓</span>{f}
          </li>
        ))}
      </ul>
      {isCurrent ? (
        <CurrentPlanBadge />
      ) : (
        <button onClick={onSelect} style={{ ...btnPrimary, width: '100%' }}>
          Claim Founder Spot
        </button>
      )}
    </div>
  )
}

function CurrentPlanBadge() {
  const navigate = useNavigate()
  return (
    <button
      type="button"
      onClick={() => navigate('/account')}
      style={{
        border: `1px solid ${PURPLE_LIGHT}`,
        background: 'rgba(124,58,237,0.10)',
        borderRadius: 8,
        padding: '12px 20px',
        color: PURPLE_LIGHT,
        textAlign: 'center',
        fontWeight: 700,
        fontSize: 14,
        width: '100%',
        boxSizing: 'border-box',
        cursor: 'pointer',
        fontFamily: 'inherit',
        transition: 'background 0.15s, border-color 0.15s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'rgba(124,58,237,0.18)'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'rgba(124,58,237,0.10)'
      }}
    >
      ✓ Your current plan — Manage →
    </button>
  )
}

const PRICING_FAQS = [
  {
    q: 'How does annual pricing work?',
    a: "Save 40% by switching to annual billing. You're still billed monthly — there's no big upfront payment — and you can cancel anytime.",
  },
  {
    q: "What's the difference between core and industry agents?",
    a: 'Core agents (10 of them) are general-purpose: writing, analysis, planning, social media. Industry agents are specialists for specific verticals — a restaurant has different needs than a law firm.',
  },
  {
    q: 'Can I change plans later?',
    a: 'Yes. Upgrade or downgrade from your dashboard. Upgrades take effect immediately with prorated billing. Downgrades take effect at the end of your current billing cycle.',
  },
  {
    q: 'What happens to founder pricing if I cancel?',
    a: 'Founder rates are locked as long as your subscription stays active. Cancellation permanently forfeits the founder rate — you cannot re-subscribe at the founder price later.',
  },
  {
    q: 'Can I switch industries on All-Access or Agency?',
    a: 'Standard subscribers can switch industries from their dashboard. Founders are locked to their original industry — switching would forfeit your founder rate.',
  },
  {
    q: 'What about the agents I already have? (legacy users)',
    a: "If you signed up before the new pricing model, you keep your current rate as long as you stay subscribed. Any plan change moves you to current pricing.",
  },
]

function PricingFAQ() {
  const [open, setOpen] = useState<number | null>(null)
  return (
    <div style={{ marginBottom: 40 }}>
      <h2 style={{
        fontFamily: 'Syne, sans-serif',
        fontSize: 28,
        fontWeight: 800,
        textAlign: 'center',
        margin: '0 0 28px',
      }}>
        Pricing FAQ
      </h2>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        {PRICING_FAQS.map((faq, idx) => {
          const isOpen = open === idx
          return (
            <div key={faq.q} style={{
              borderBottom: `1px solid ${BORDER}`,
              padding: '16px 0',
            }}>
              <button
                onClick={() => setOpen(isOpen ? null : idx)}
                style={{
                  width: '100%',
                  background: 'none',
                  border: 'none',
                  color: '#fff',
                  fontFamily: 'inherit',
                  fontSize: 15,
                  fontWeight: 600,
                  textAlign: 'left',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                <span>{faq.q}</span>
                <span style={{ color: PURPLE_LIGHT, marginLeft: 12 }}>{isOpen ? '−' : '+'}</span>
              </button>
              {isOpen && (
                <p style={{ color: TEXT_MUTED, fontSize: 14, lineHeight: 1.6, margin: '12px 0 0' }}>
                  {faq.a}
                </p>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function PricingSkeleton() {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: 20,
    }}>
      {[1, 2, 3].map((n) => (
        <div key={n} style={{
          background: SURFACE,
          border: `1px solid ${BORDER}`,
          borderRadius: 14,
          padding: 28,
          minHeight: 360,
          opacity: 0.6,
        }}>
          <div style={{ height: 24, background: 'rgba(255,255,255,0.08)', borderRadius: 4, marginBottom: 12 }} />
          <div style={{ height: 12, width: '70%', background: 'rgba(255,255,255,0.06)', borderRadius: 4, marginBottom: 28 }} />
          <div style={{ height: 42, width: '50%', background: 'rgba(255,255,255,0.10)', borderRadius: 4, marginBottom: 24 }} />
          {[1, 2, 3, 4].map((i) => (
            <div key={i} style={{
              height: 12,
              background: 'rgba(255,255,255,0.05)',
              borderRadius: 4,
              marginBottom: 10,
              width: i === 3 ? '60%' : '85%',
            }} />
          ))}
        </div>
      ))}
    </div>
  )
}

const btnPrimary: React.CSSProperties = {
  background: PURPLE,
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  padding: '12px 20px',
  fontWeight: 700,
  fontSize: 15,
  cursor: 'pointer',
  fontFamily: 'inherit',
}

const btnSecondary: React.CSSProperties = {
  background: 'transparent',
  color: '#fff',
  border: `1px solid ${BORDER}`,
  borderRadius: 8,
  padding: '10px 16px',
  fontWeight: 600,
  fontSize: 14,
  cursor: 'pointer',
  fontFamily: 'inherit',
}
